/**
 * In-app camera capture — pure controller (R23 C2).
 *
 * Field learning (dev-89, turns 35–48): on the kiosk device the Card→Camera option opened a FILE
 * PICKER, not the camera. Desktop-class / kiosk browsers ignore the `capture` attribute on
 * `<input type=file>`, so "Take photo" fell through to a file browser and the visitor was stranded.
 * R23 C2 builds a REAL in-app camera surface (`getUserMedia` live preview → capture a frame → the
 * existing upload path). This module is the PURE, node-testable core of that surface: the state
 * machine + `mediaDevices` classification + the frame→File encode, with NO React and NO direct DOM
 * beyond the two elements it's handed (a `<video>` to attach the stream to, a `<canvas>` to grab from).
 * The kiosk vitest suite has no jsdom, so keeping this logic pure lets us test start/stop/capture and
 * every fallback by MOCKING `navigator.mediaDevices` — exactly what the C2 handoff asks for.
 *
 * Fallback matrix (the reason each state exists):
 *   - `unsupported`  — no `navigator.mediaDevices.getUserMedia` at all (old webview) → file-picker route
 *   - `denied`       — the visitor (or a kiosk policy) refused the permission → file-picker route
 *   - `no-device`    — the API exists + permission is fine but there's no camera → file-picker route
 *   - `error`        — any other getUserMedia failure → file-picker route
 * The modal offers the file-picker escape with an honest hint line in every non-live state.
 */

export type CameraState =
  | 'idle'
  | 'requesting'
  | 'live'
  | 'capturing'
  | 'denied'
  | 'no-device'
  | 'unsupported'
  | 'error';

/** A non-live terminal state where the modal should offer the file-picker fallback + an honest hint. */
export type CameraFallbackReason = 'denied' | 'no-device' | 'unsupported' | 'error';

/** True when the state is a fallback state (camera can't be used → route to the file picker). */
export function isFallbackState(s: CameraState): s is CameraFallbackReason {
  return s === 'denied' || s === 'no-device' || s === 'unsupported' || s === 'error';
}

/**
 * An honest, non-technical hint line for each fallback state — shown next to the file-picker escape.
 * Presentation copy lives here (one source) so the modal and any tests read the same strings.
 */
export const FALLBACK_HINTS: Record<CameraFallbackReason, string> = {
  denied: "Camera access is off, so we can't open it here. You can pick a photo instead.",
  'no-device': 'No camera found on this device. You can pick a photo instead.',
  unsupported: "This device can't open the camera in-app. You can pick a photo instead.",
  error: "The camera didn't start. You can pick a photo instead — or try again.",
};

/** True if the environment can even attempt an in-app camera (guards SSR / old webviews). */
export function cameraSupported(md: MediaDevices | undefined | null): md is MediaDevices {
  return !!md && typeof md.getUserMedia === 'function';
}

/**
 * Map a `getUserMedia` rejection to a fallback state. Uses the standard DOMException `name`s:
 *   - NotAllowedError / SecurityError → `denied`
 *   - NotFoundError / OverconstrainedError / DevicesNotFoundError → `no-device`
 *   - everything else → `error`
 * Tolerant of non-Error throwables (returns `error`).
 */
export function classifyCameraError(err: unknown): CameraFallbackReason {
  const name =
    err && typeof err === 'object' && 'name' in err ? String((err as { name: unknown }).name) : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (
    name === 'NotFoundError' ||
    name === 'DevicesNotFoundError' ||
    name === 'OverconstrainedError'
  ) {
    return 'no-device';
  }
  return 'error';
}

/** Constraints for the rear/environment camera (a booth kiosk scans a card in front of it). */
export const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: { facingMode: { ideal: 'environment' } },
  audio: false,
};

export type StartResult =
  | { ok: true; stream: MediaStream }
  | { ok: false; reason: CameraFallbackReason };

/**
 * Attempt to open the camera stream. Pure w.r.t. React — takes the `MediaDevices` so it's trivially
 * mockable. Returns a discriminated result the caller turns into a `CameraState` (never throws).
 */
export async function startCamera(md: MediaDevices | undefined | null): Promise<StartResult> {
  if (!cameraSupported(md)) return { ok: false, reason: 'unsupported' };
  try {
    const stream = await md.getUserMedia(CAMERA_CONSTRAINTS);
    return { ok: true, stream };
  } catch (err) {
    return { ok: false, reason: classifyCameraError(err) };
  }
}

/** Stop every track on a stream (idempotent, null-safe) — always call on unmount / after capture. */
export function stopStream(stream: MediaStream | null | undefined): void {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    track.stop();
  }
}

/** Default encode settings for the captured frame (JPEG is small + universally accepted by the OCR). */
export const CAPTURE_MIME = 'image/jpeg';
export const CAPTURE_QUALITY = 0.92;
export const CAPTURE_FILENAME = 'card-photo.jpg';

/**
 * Draw the current video frame onto the canvas and encode it to a `File`, reusing the SAME downstream
 * upload/OCR path a picked file uses. Returns null if the video has no frame yet (0×0) or the browser
 * can't encode (`toBlob` → null). Pure logic; the caller supplies the two DOM elements.
 */
export function captureFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement
): Promise<File | null> {
  return new Promise((resolve) => {
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      resolve(null);
      return;
    }
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      resolve(null);
      return;
    }
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        resolve(new File([blob], CAPTURE_FILENAME, { type: CAPTURE_MIME }));
      },
      CAPTURE_MIME,
      CAPTURE_QUALITY
    );
  });
}
